import type { ReactNode } from 'react';
import { Switch } from '../Switch/index.js';
import { Segmented } from '../Segmented/index.js';
import type { SettingsCategory } from './types.js';
import type { RenderingId } from '../../theme/types.js';

export interface AppearanceSectionProps {
  theme: string;
  themeOptions: ReadonlyArray<{ value: string; label: string }>;
  onThemeChange: (theme: string) => void;
  density: 'compact' | 'comfortable' | 'spacious';
  onDensityChange: (density: 'compact' | 'comfortable' | 'spacious') => void;
  rendering?: RenderingId;
  onRenderingChange?: (rendering: RenderingId) => void;
  reducedMotion?: boolean;
  onReducedMotionChange?: (next: boolean) => void;
}

/** Appearance tab — theme · density · rendering · reduced-motion. Spec 10 §2. */
export function AppearanceSection(props: AppearanceSectionProps): ReactNode {
  const {
    theme,
    themeOptions,
    onThemeChange,
    density,
    onDensityChange,
    rendering,
    onRenderingChange,
    reducedMotion,
    onReducedMotionChange,
  } = props;
  return (
    <div className="vq-settings-section">
      <SettingRow label="Theme" description="Light, dark, or high-contrast palettes.">
        <Segmented
          options={themeOptions.map((o) => ({ value: o.value, label: o.label }))}
          value={theme}
          onChange={onThemeChange}
        />
      </SettingRow>
      <SettingRow label="Density" description="How compact the UI chrome is.">
        <Segmented
          options={[
            { value: 'compact', label: 'Compact' },
            { value: 'comfortable', label: 'Comfortable' },
            { value: 'spacious', label: 'Spacious' },
          ]}
          value={density}
          onChange={(v: string) => {
            onDensityChange(v as 'compact' | 'comfortable' | 'spacious');
          }}
        />
      </SettingRow>
      {onRenderingChange !== undefined ? (
        <SettingRow
          label="Rendering"
          description="Trade expensive blur, shadows, and ambient effects against paint cost."
        >
          <Segmented
            options={[
              { value: 'performance', label: 'Performance' },
              { value: 'balanced', label: 'Balanced' },
              { value: 'quality', label: 'Quality' },
            ]}
            value={rendering ?? 'balanced'}
            onChange={(v: string) => {
              onRenderingChange(v as RenderingId);
            }}
          />
        </SettingRow>
      ) : null}
      {onReducedMotionChange !== undefined ? (
        <SettingRow label="Reduce motion" description="Minimize animations throughout the app.">
          <Switch
            checked={reducedMotion === true}
            onCheckedChange={(next: boolean) => {
              onReducedMotionChange(next);
            }}
          />
        </SettingRow>
      ) : null}
    </div>
  );
}

export interface LanguageSectionProps {
  locale: string;
  localeOptions: ReadonlyArray<{ value: string; label: string }>;
  onLocaleChange: (locale: string) => void;
}

export function LanguageSection(props: LanguageSectionProps): ReactNode {
  const { locale, localeOptions, onLocaleChange } = props;
  return (
    <div className="vq-settings-section">
      <SettingRow label="Language" description="Interface language and date formatting locale.">
        <Segmented
          options={localeOptions.map((o) => ({ value: o.value, label: o.label }))}
          value={locale}
          onChange={onLocaleChange}
        />
      </SettingRow>
    </div>
  );
}

export interface LayoutSectionProps {
  preset: string;
  onPresetChange: (preset: string) => void;
  sidebarVisible: boolean;
  onSidebarVisibleChange: (next: boolean) => void;
  statusBarVisible: boolean;
  onStatusBarVisibleChange: (next: boolean) => void;
}

export function LayoutSection(props: LayoutSectionProps): ReactNode {
  const {
    preset,
    onPresetChange,
    sidebarVisible,
    onSidebarVisibleChange,
    statusBarVisible,
    onStatusBarVisibleChange,
  } = props;
  return (
    <div className="vq-settings-section">
      <SettingRow label="Preset" description="Opinionated layouts for common tasks.">
        <Segmented
          options={[
            { value: 'writing', label: 'Writing' },
            { value: 'reading', label: 'Reading' },
            { value: 'focus', label: 'Focus' },
            { value: 'review', label: 'Review' },
          ]}
          value={preset}
          onChange={onPresetChange}
        />
      </SettingRow>
      <SettingRow label="Sidebar">
        <Switch
          checked={sidebarVisible}
          onCheckedChange={(next: boolean) => {
            onSidebarVisibleChange(next);
          }}
        />
      </SettingRow>
      <SettingRow label="Status bar">
        <Switch
          checked={statusBarVisible}
          onCheckedChange={(next: boolean) => {
            onStatusBarVisibleChange(next);
          }}
        />
      </SettingRow>
    </div>
  );
}

export interface AccessibilitySectionProps {
  reducedMotion: boolean;
  onReducedMotionChange: (next: boolean) => void;
  highContrast: boolean;
  onHighContrastChange: (next: boolean) => void;
  largerText: boolean;
  onLargerTextChange: (next: boolean) => void;
}

export function AccessibilitySection(props: AccessibilitySectionProps): ReactNode {
  const {
    reducedMotion,
    onReducedMotionChange,
    highContrast,
    onHighContrastChange,
    largerText,
    onLargerTextChange,
  } = props;
  return (
    <div className="vq-settings-section">
      <SettingRow label="Reduce motion" description="Collapse animations and transitions.">
        <Switch
          checked={reducedMotion}
          onCheckedChange={(next: boolean) => {
            onReducedMotionChange(next);
          }}
        />
      </SettingRow>
      <SettingRow label="High contrast" description="Increase the contrast of UI chrome.">
        <Switch
          checked={highContrast}
          onCheckedChange={(next: boolean) => {
            onHighContrastChange(next);
          }}
        />
      </SettingRow>
      <SettingRow label="Larger text" description="Increase base font size.">
        <Switch
          checked={largerText}
          onCheckedChange={(next: boolean) => {
            onLargerTextChange(next);
          }}
        />
      </SettingRow>
    </div>
  );
}

export interface PrivacySectionProps {
  telemetry: boolean;
  onTelemetryChange: (next: boolean) => void;
  crashReports: boolean;
  onCrashReportsChange: (next: boolean) => void;
}

export function PrivacySection(props: PrivacySectionProps): ReactNode {
  const { telemetry, onTelemetryChange, crashReports, onCrashReportsChange } = props;
  return (
    <div className="vq-settings-section">
      <SettingRow label="Usage telemetry" description="Share anonymized feature usage.">
        <Switch
          checked={telemetry}
          onCheckedChange={(next: boolean) => {
            onTelemetryChange(next);
          }}
        />
      </SettingRow>
      <SettingRow label="Crash reports" description="Send diagnostic info after crashes.">
        <Switch
          checked={crashReports}
          onCheckedChange={(next: boolean) => {
            onCrashReportsChange(next);
          }}
        />
      </SettingRow>
    </div>
  );
}

export interface SettingRowProps {
  label: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}

/** Narrow two-column row used inside every default settings section. */
export function SettingRow(props: SettingRowProps): ReactNode {
  return (
    <div className="vq-settings-row">
      <div className="vq-settings-row__meta">
        <div className="vq-settings-row__label">{props.label}</div>
        {props.description !== undefined ? (
          <div className="vq-settings-row__desc">{props.description}</div>
        ) : null}
      </div>
      <div className="vq-settings-row__control">{props.children}</div>
    </div>
  );
}

/** Helper to build the Phase 1 category list from handler-shaped props. */
export function buildPhase1Categories(config: {
  appearance: AppearanceSectionProps;
  language: LanguageSectionProps;
  layout: LayoutSectionProps;
  accessibility: AccessibilitySectionProps;
  privacy: PrivacySectionProps;
}): ReadonlyArray<SettingsCategory> {
  return [
    {
      id: 'appearance',
      label: 'Appearance',
      render: () => <AppearanceSection {...config.appearance} />,
    },
    { id: 'language', label: 'Language', render: () => <LanguageSection {...config.language} /> },
    { id: 'layout', label: 'Layout', render: () => <LayoutSection {...config.layout} /> },
    {
      id: 'accessibility',
      label: 'Accessibility',
      render: () => <AccessibilitySection {...config.accessibility} />,
    },
    { id: 'privacy', label: 'Privacy', render: () => <PrivacySection {...config.privacy} /> },
  ];
}
